'use client';

/**
 * @fileoverview Newsletter component for the marketing feature.
 */
import { useState, useRef } from 'react';
import { Loader2, CheckCircle, Send } from 'lucide-react';
import { apiUrl } from '@/lib/config';
import { trackEvent } from '@/lib/analytics/events';
import { CloudflareTurnstile } from '@/components/security/CloudflareTurnstile';

export function Newsletter({ variant = 'simple' }: { variant?: 'simple' | 'full' }) {
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  async function handleSubmit(formData: FormData) {
    setIsPending(true);
    setStatus(null);

    trackEvent('form_start', {
      form_name: 'newsletter',
      form_location: variant,
    });
    
    try {
      const email = (formData.get('email') as string)?.trim() || '';
      const website = (formData.get('website') as string)?.trim() || '';

      const response = await fetch(apiUrl('/api/subscribe'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          website: website || undefined,
          turnstileToken: turnstileToken || undefined,
        }),
      });
      
      const result = await response.json().catch(() => ({}));
      
      if (!response.ok) {
        setStatus({ type: 'error', message: result.error || 'Failed to subscribe' });
        trackEvent('error_event', {
          form_name: 'newsletter',
          error_type: 'newsletter_subscription_failed',
        });
      } else if (result.success) {
        setStatus({ type: 'success', message: result.message || 'Subscribed successfully!' });
        formRef.current?.reset();
        setTurnstileToken(null);
        trackEvent('form_submit', {
          form_name: 'newsletter',
          form_location: variant,
        });
        trackEvent('newsletter_signup', {
          form_location: variant,
        });
      }
    } catch {
      setStatus({ type: 'error', message: 'An unexpected error occurred.' });
    } finally {
      setIsPending(false);
    }
  }

  if (variant === 'simple') {
    return (
      <div className="space-y-3">
        <form
          ref={formRef}
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit(new FormData(e.currentTarget));
          }}
          className="flex flex-col gap-2"
        >
          {/* Honeypot field for bot spam prevention */}
          <div className="hidden" aria-hidden="true" style={{ display: 'none' }}>
            <label htmlFor="newsletter-hp-simple">Leave this blank</label>
            <input
              id="newsletter-hp-simple"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <label htmlFor="newsletter-email" className="sr-only">
            Email address
          </label>
          <div className="relative">
            <input
              id="newsletter-email"
              name="email"
              type="email"
              autoComplete="email"
              aria-label="Email address"
              placeholder="Email address"
              required
              className="w-full rounded-xl border border-outline-variant bg-pb-white px-4 py-3 text-[14px] text-pb-slate-900 placeholder:text-pb-slate-400 transition-all focus:border-pb-blue-500 focus:ring-1 focus:ring-pb-blue-500 outline-none dark:border-pb-white/[0.08] dark:bg-[#0b1120] dark:text-pb-white dark:placeholder:text-pb-slate-500"
            />
            <button
              type="submit"
              disabled={isPending}
              aria-label="Subscribe to newsletter"
              className="absolute top-2 right-2 rounded-lg bg-pb-blue-600 p-1.5 transition-colors hover:bg-pb-blue-700 disabled:opacity-50 flex items-center justify-center"
            >
              {isPending ? (
                <Loader2 size={15} className="animate-spin text-pb-white" />
              ) : (
                <Send size={15} className="text-pb-white" />
              )}
            </button>
          </div>
          {status && (
            <p
              className={`text-[12px] font-medium ${status.type === 'success' ? 'text-emerald-500 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'}`}
            >
              {status.message}
            </p>
          )}
        </form>
      </div>
    );
  }


  // "Full" variant for the Resources page...
  return (
    <div className="mx-auto max-w-7xl px-6 py-24">
      {/* Updated Newsletter Section */}
      <div className="relative mt-20 overflow-hidden rounded-[3rem] bg-pb-slate-950 bg-gradient-to-b from-pb-blue-900/50 to-transparent p-12 text-center text-pb-white">
        <div className="absolute top-0 right-0 h-64 w-64 bg-pb-blue-600/20 blur-[100px]" />

        <h3 className="relative z-10 mb-4 text-3xl font-bold">Stay updated on new features</h3>
        <p className="relative z-10 mx-auto mb-8 max-w-md text-pb-slate-300">
          Get notified when we release new templates or update the export engine.
        </p>

        <form
          ref={formRef}
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit(new FormData(e.currentTarget));
          }}
          className="relative z-10 flex flex-col items-center gap-4"
        >
          {/* Honeypot field for bot spam prevention */}
          <div className="hidden" aria-hidden="true" style={{ display: 'none' }}>
            <label htmlFor="newsletter-hp-full">Leave this blank</label>
            <input
              id="newsletter-hp-full"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="flex w-full flex-col justify-center gap-3 sm:flex-row">
            <label htmlFor="newsletter-email-full" className="sr-only">
              Email address
            </label>
            <input
              id="newsletter-email-full"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder="Enter your email"
              className="w-full max-w-xs rounded-2xl border border-pb-white/10 bg-pb-white/10 px-6 py-4 text-pb-white transition-all outline-none placeholder:text-pb-slate-400 focus:border-pb-blue-500"
            />
            <button
              disabled={isPending}
              type="submit"
              className="flex items-center justify-center gap-2 rounded-2xl bg-pb-blue-600 px-8 py-4 font-bold shadow-lg shadow-pb-blue-600/20 transition-all hover:bg-pb-blue-700 disabled:opacity-50"
            >
              {isPending ? <Loader2 className="animate-spin" size={20} /> : 'Subscribe'}
            </button>
          </div>

          {/* Cloudflare Turnstile CAPTCHA */}
          <CloudflareTurnstile
            action="newsletter_subscribe"
            theme="dark"
            onVerify={(token) => setTurnstileToken(token)}
            onExpire={() => setTurnstileToken(null)}
            onError={() => setTurnstileToken(null)}
          />

          {/* Feedback Messages */}
          {status && (
            <div
              className={`animate-in fade-in slide-in-from-top-2 mt-4 flex items-center gap-2 text-sm font-medium ${status.type === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}
            >
              {status.type === 'success' && <CheckCircle size={16} />}
              {status.message}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
